/**
 * DosingHistorySection - Shows dosing history relevant to a specific parameter.
 *
 * Displayed on the ParameterDetailPage to correlate parameter readings with
 * dosing activity. Shows a stacked bar chart of daily dosing volumes and a
 * compact recent events list for chemicals that affect the parameter.
 *
 * Chemical-to-parameter mapping covers:
 * - Alkalinity: Alk supplements, two-part, all-in-one, kalkwasser
 * - Calcium: Ca supplements, two-part Ca/Mg, all-in-one, kalkwasser
 * - Magnesium: Mg supplements, two-part Ca/Mg, all-in-one
 * - Nitrate: NoPox, vodka, vinegar, ammonia, nitrate supplements
 * - Phosphate: NoPox, lanthanum chloride, phosphate supplements
 */
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Paper,
  Text,
  Stack,
  Group,
  Badge,
  Anchor,
  Center,
  Loader,
} from '@mantine/core';
import { IconDroplet, IconChartBar } from '@tabler/icons-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { format, eachDayOfInterval } from 'date-fns';
import type { DosingPoint } from '@/hooks/useAnalysisData';
import { ANALYSIS_DOSING_COLORS } from '@/utils/chartColors';
import { CHART_GRID_STROKE, CHART_AXIS_TICK, CHART_LEGEND_STYLE } from '@/utils/chartStyles';
import {
  TooltipContainer,
  TooltipHeader,
  TooltipRow,
  TooltipFooter,
} from '@/features/analysis/components/ChartTooltip';

/**
 * Maps parameter keys to arrays of chemical names (case-insensitive match).
 * Each entry lists chemicals that either raise or lower that parameter.
 */
const PARAMETER_CHEMICALS: Record<string, string[]> = {
  alkalinity: [
    'Alkalinity',
    'Two-Part Alkalinity',
    'All-In-One',
    'Kalkwasser',
  ],
  ca: [
    'Calcium',
    'Two-Part Calcium/Magnesium',
    'All-In-One',
    'Kalkwasser',
  ],
  mg: [
    'Magnesium',
    'Two-Part Calcium/Magnesium',
    'All-In-One',
  ],
  no3: [
    'Nitrate',
    'NoPox',
    'Vodka',
    'Vinegar',
    'Ammonia',
  ],
  po4: [
    'Phosphate',
    'NoPox',
    'Lanthanum Chloride',
  ],
};

interface ChartDataPoint {
  date: string;
  timestamp: number;
  displayDate: string;
  [chemical: string]: string | number;
}

function DosingTooltip({
  active,
  payload,
}: {
  active?: boolean;
  payload?: Array<{ name: string; value: number; fill: string; payload?: ChartDataPoint }>;
  label?: string;
}): JSX.Element | null {
  if (!active || !payload || payload.length === 0) return null;

  const dateString = payload[0]?.payload?.date;
  const total = payload.reduce((sum, entry) => sum + (entry.value || 0), 0);

  return (
    <TooltipContainer>
      {dateString && (
        <TooltipHeader timestamp={`${dateString}T12:00:00`} showTime={false} />
      )}
      {payload.map((entry) => (
        <TooltipRow
          key={entry.name}
          color={entry.fill}
          label={entry.name}
          value={(entry.value ?? 0).toFixed(1)}
          unit="mL"
        />
      ))}
      {payload.length > 1 && (
        <TooltipFooter>Total: {total.toFixed(1)} mL</TooltipFooter>
      )}
    </TooltipContainer>
  );
}

interface DosingHistorySectionProps {
  parameterKey: string;
  dosing: Record<string, DosingPoint[]>;
  availableDosing: string[];
  isLoading: boolean;
  isMobile: boolean;
  timeDomain?: { startTs: number; endTs: number };
}

export function DosingHistorySection({
  parameterKey,
  dosing,
  availableDosing,
  isLoading,
  isMobile,
  timeDomain,
}: DosingHistorySectionProps) {
  // Get the list of chemical names relevant to this parameter
  const knownChemicals = PARAMETER_CHEMICALS[parameterKey];

  // Match available dosing chemicals against the known list (case-insensitive)
  const matchedChemicals = useMemo(() => {
    if (!knownChemicals || !availableDosing) return [];

    const knownLower = knownChemicals.map((c) => c.toLowerCase());
    return availableDosing.filter((name) =>
      knownLower.includes(name.toLowerCase())
    );
  }, [knownChemicals, availableDosing]);

  // Build chart data with a data point for every day in the time domain
  const chartData = useMemo(() => {
    if (matchedChemicals.length === 0) return [];

    // First, collect dosing data keyed by date
    const dateMap = new Map<string, ChartDataPoint>();

    for (const chemical of matchedChemicals) {
      const points = dosing[chemical] || [];
      for (const point of points) {
        if (!dateMap.has(point.date)) {
          dateMap.set(point.date, {
            date: point.date,
            timestamp: new Date(`${point.date}T12:00:00`).getTime(),
            displayDate: format(new Date(`${point.date}T12:00:00`), 'MMM d'),
          });
        }
        const row = dateMap.get(point.date)!;
        row[chemical] = point.volume_ml;
      }
    }

    if (dateMap.size === 0) return [];

    // Fill in every day in the range so bars are each exactly one day wide
    const start = timeDomain
      ? new Date(timeDomain.startTs)
      : new Date(Math.min(...Array.from(dateMap.values()).map((d) => d.timestamp)));
    const end = timeDomain
      ? new Date(timeDomain.endTs)
      : new Date(Math.max(...Array.from(dateMap.values()).map((d) => d.timestamp)));

    return eachDayOfInterval({ start, end }).map((day) => {
      const dateStr = format(day, 'yyyy-MM-dd');
      return dateMap.get(dateStr) ?? {
        date: dateStr,
        timestamp: new Date(`${dateStr}T12:00:00`).getTime(),
        displayDate: format(day, 'MMM d'),
      };
    });
  }, [matchedChemicals, dosing, timeDomain]);

  // Don't render if this parameter has no chemical mappings
  if (!knownChemicals) return null;

  // Don't render if loading
  if (isLoading) {
    return (
      <Paper p="md" withBorder role="region" aria-label="Dosing History">
        <Group gap="xs" mb="md">
          <IconDroplet size={18} />
          <Text fw={600} size="sm">Dosing History</Text>
        </Group>
        <Center h={120}>
          <Loader size="sm" />
        </Center>
      </Paper>
    );
  }

  // No dosing data found for related chemicals
  if (matchedChemicals.length === 0 || chartData.length === 0) {
    const emptyMessage = matchedChemicals.length === 0
      ? 'No dosing data found for chemicals that affect this parameter'
      : `No dosing data in the selected time range for ${matchedChemicals.join(', ')}`;

    return (
      <Paper p="md" withBorder role="region" aria-label="Dosing History">
        <Group gap="xs" mb="md">
          <IconDroplet size={18} />
          <Text fw={600} size="sm">Dosing History</Text>
        </Group>
        <Center py="lg">
          <Stack align="center" gap="xs">
            <Text size="sm" c="dimmed">
              {emptyMessage}
            </Text>
            <Text size="xs" c="dimmed">
              Related chemicals: {knownChemicals.join(', ')}
            </Text>
            <Anchor component={Link} to="/dosing" size="sm">
              Go to Dosing page
            </Anchor>
          </Stack>
        </Center>
      </Paper>
    );
  }

  return (
    <Paper p="md" withBorder role="region" aria-label="Dosing History">
      <Group justify="space-between" mb="md">
        <Group gap="xs">
          <IconDroplet size={18} />
          <Text fw={600} size="sm">Dosing History</Text>
        </Group>
        <Anchor component={Link} to="/dosing" size="sm" c="dimmed">
          <Group gap={4} wrap="nowrap">
            <IconChartBar size={14} />
            View All Dosing
          </Group>
        </Anchor>
      </Group>

      {/* Chemical badges */}
      <Group gap="xs" mb="sm">
        {matchedChemicals.map((chemical, index) => (
          <Badge
            key={chemical}
            size="sm"
            variant="light"
            leftSection={
              <div
                aria-hidden="true"
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: 2,
                  backgroundColor: ANALYSIS_DOSING_COLORS[index % ANALYSIS_DOSING_COLORS.length],
                }}
              />
            }
          >
            {chemical}
          </Badge>
        ))}
      </Group>

      {/* Bar chart */}
      <ResponsiveContainer width="100%" height={isMobile ? 160 : 200}>
        <BarChart data={chartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID_STROKE} vertical={false} />
          <XAxis
            dataKey="timestamp"
            type="number"
            domain={timeDomain ? [timeDomain.startTs, timeDomain.endTs] : ['dataMin', 'dataMax']}
            tickFormatter={(value) => format(new Date(value), isMobile ? 'M/d' : 'MMM d')}
            tick={CHART_AXIS_TICK}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            label={
              !isMobile
                ? { value: 'mL', angle: -90, position: 'insideLeft', style: { fontSize: 11 } }
                : undefined
            }
            tick={CHART_AXIS_TICK}
            width={40}
            axisLine={false}
            tickLine={false}
          />
          <Tooltip
            content={<DosingTooltip />}
            cursor={{ fill: 'rgba(148, 163, 184, 0.1)' }}
          />
          {matchedChemicals.length > 1 && (
            <Legend
              verticalAlign="bottom"
              height={30}
              wrapperStyle={CHART_LEGEND_STYLE}
            />
          )}
          {matchedChemicals.map((chemical, index) => (
            <Bar
              key={chemical}
              dataKey={chemical}
              name={chemical}
              fill={ANALYSIS_DOSING_COLORS[index % ANALYSIS_DOSING_COLORS.length]}
              stackId="dosing"
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </Paper>
  );
}
